import { createApi } from '@reduxjs/toolkit/query/react';
import { LookupItems, QuizDetailsMeta, QuizMeta } from '../../types/quiz';
import { setSkillName } from '../slices/QuizSlice';
import { baseQueryWithReauth } from './baseQuery';

export const quizService = createApi({
  reducerPath: 'quizService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['quizList'],

  endpoints: (builder) => ({
    getNumOfQuestions: builder.query<{ payload: LookupItems[] }, void>({
      query: () => ({
        url: '/quiz-service/v1/users/quiz-number-of-questions',
        method: 'GET',
      }),
    }),

    getDurations: builder.query<{ payload: LookupItems[] }, void>({
      query: () => ({
        url: '/quiz-service/v1/quiz-duration',
        method: 'GET',
      }),
    }),

    addQuiz: builder.mutation({
      query: ({ body }) => ({
        url: '/quiz-service/v1/quizzes-operator/add-quiz',
        method: 'POST',
        body,
      }),

      invalidatesTags: ['quizList'],
    }),

    updateQuiz: builder.mutation({
      query: ({ id, body }) => ({
        url: '/quiz-service/v1/quizzes-operator/edit-quiz',
        method: 'PATCH',
        body: {
          quizId: id,
          ...body,
        },
      }),
    }),

    getAllQuizzes: builder.query<
      {
        payload: {
          content: QuizMeta[];
          totalElements: number;
        };
      },
      { page: number; filters?: { statusCode: string; requestIds: number } }
    >({
      query: ({ page, filters }) => ({
        url: '/quiz-service/v1/quizzes-operator',
        method: 'GET',
        params: {
          page,
          ...filters,
        },
      }),

      providesTags: ['quizList'],
    }),

    // For select skill form
    getCategories: builder.query<{ payload: LookupItems[] }, void>({
      query: () => ({
        url: '/v1/user-operator/categories',
        method: 'GET',
      }),
    }),

    getSpecialities: builder.query<
      { payload: LookupItems[] },
      { categoryId: string }
    >({
      query: ({ categoryId }) => ({
        url: `/v1/user-operator/specialities?category-id=${categoryId}`,
        method: 'GET',
      }),
    }),

    getSkills: builder.query<
      { payload: LookupItems[] },
      { specialityId: number; skillId?: string }
    >({
      query: ({ specialityId }) => ({
        url: `/v1/user-operator/skills?speciality-id=${specialityId}`,
        method: 'GET',
      }),
      async onQueryStarted(queryArgument, queryLifeCycleApi) {
        if (!!queryArgument?.skillId) {
          const response = await queryLifeCycleApi.queryFulfilled;
          const selectedSkill = response?.data?.payload?.find(
            (skill) => skill.id == queryArgument?.skillId
          );

          queryLifeCycleApi.dispatch(setSkillName(selectedSkill?.name));
        }
      },
    }),

    getQuizDetails: builder.query<{ payload: QuizDetailsMeta }, string>({
      query: (id) => ({
        url: `/quiz-service/v1/quizzes-operator/quizzes/${id}`,
        method: 'GET',
      }),
    }),

    checkSkill: builder.query({
      query: (skillId) => ({
        url: `/quiz-service/v1/quizzes-operator/check-quiz/${skillId}`,
        method: 'GET',
      }),
    }),

    getAllSpecialities: builder.query<
      {
        payload: {
          id: number;
          name: string;
          nameAr: string;
          nameEn: string;
        }[];
      },
      void
    >({
      query: () => ({
        url: '/v1/specialities/all',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useAddQuizMutation,
  useGetAllQuizzesQuery,
  useGetDurationsQuery,
  useGetNumOfQuestionsQuery,
  useGetCategoriesQuery,
  useGetSpecialitiesQuery,
  useGetSkillsQuery,
  useGetQuizDetailsQuery,
  useUpdateQuizMutation,
  useLazyCheckSkillQuery,
  useGetAllSpecialitiesQuery,
} = quizService;
